import { saleApi } from '@/app/api/saleApi';
import { getClientId } from '@/utils/clientId';
import type { TableSession } from './tableSessionSlice';

// ---------------------------------------------------------------------------
// API response shapes from sale_api
// ---------------------------------------------------------------------------

export interface ResolveTableResponse {
  // Fix 12: server sends CharField string; type consistently as string
  table_number: string | number;
  capacity: number;
  // Per-table covers ceiling (capacity + buffer) bounding the entry headcount
  // picker. Optional so an older server (no field) degrades gracefully.
  max_covers?: number;
  mode: 'ordering' | 'buffet';
  staff_open_required: boolean;
  session_open: boolean;
}

export interface ResolveTableArgs {
  restaurantUuid: string;
  tableToken: string;
}

interface RawSessionState {
  uuid: string;
  // Fix 12: server sends CharField string; coerce to string at transform boundary
  table_number: string | number;
  mode: 'ordering' | 'buffet';
  covers: number;
  round_count: number;
  cooldown_seconds_remaining: number;
  expires_at: string;
  // SC-169: shared table cart mode (absent on older servers → false)
  shared_table_cart_enabled?: boolean;
}

// SC-169 — shared table cart wire shapes (one server-side cart per table session).
export interface SharedCartLine {
  id: string;
  product_uuid: string;
  quantity: number;
  options: unknown[];
  comment: string;
  added_by_guest_uuid: string;
  added_by_nickname: string;
}

export interface SharedCart {
  lines: SharedCartLine[];
}

interface JoinTableSessionRawResponse {
  guest_token: string;
  session: RawSessionState;
}

export interface JoinTableSessionArgs {
  restaurantUuid: string;
  tableToken: string;
  covers?: number;
  nickname?: string;
  /** Existing guest JWT to attempt idempotent rejoin */
  existingGuestToken?: string | null;
}

export interface JoinTableSessionResult {
  session: TableSession;
}

export interface GetTableSessionArgs {
  restaurantUuid: string;
  sessionUuid: string;
  guestToken: string;
}

export interface OrderLine {
  name: string;
  qte: number;
  options: Array<{ name?: string; label?: string; qty?: number }>;
  // Optional while the frontend and API roll out independently.
  comment?: string;
  total_ttc: string;
}

export interface TableOrder {
  uuid: string;
  round_number: number;
  online_status: string;
  payment_status: string;
  created_at: string;
  guest_nickname: string;
  lines: OrderLine[];
  total_ttc: string;
}

// Flat response shape after transformResponse flattening
export interface TableSessionCurrentResponse {
  uuid: string;
  // Fix 12: string to match server CharField
  table_number: string | number;
  // SC-513: identifiers of the table the session sits on RIGHT NOW — the QR
  // `qrcode` and the table uuid, the two things a dine-in URL's :tableToken can
  // be. Optional: an older server doesn't send them, and the client then keeps
  // its pre-SC-513 behaviour instead of dropping every session.
  table_token?: string | null;
  table_uuid?: string | null;
  // SC-976: every identifier the session's tables answer to right now — the
  // anchor's own token/uuid is included, so a single-table session's list is
  // just [that one]. Optional: an older server sends neither, and TableRoute
  // falls back to the singular table_token/table_uuid fields above.
  table_tokens?: string[] | null;
  table_uuids?: string[] | null;
  mode: 'ordering' | 'buffet';
  covers: number;
  round_count: number;
  cooldown_seconds_remaining: number;
  expires_at: string;
  shared_table_cart_enabled: boolean;
  // Spec 2026-08-28 paid-table-ordering-lock — la table a ete soldee au QR, le
  // convive n'y commande plus. Optionnel : un serveur plus ancien ne l'envoie
  // pas, et l'absence vaut « ouverte » (jamais bloquer sur un champ manquant).
  ordering_locked?: boolean;
  // DF-0586 — un service de la table attend le « Lancer » du serveur : la
  // table a déjà choisi « service par service », le site ne repose pas la
  // question. Optionnel : absent d'un serveur plus ancien = rien n'attend.
  holds_a_service?: boolean;
  // SC-221/223: buffet round limits for cart-time gating (null outside buffet).
  // Effective per-round item budget (covers-scaled server-side) and max rounds.
  buffet_round_item_budget?: number | null;
  buffet_max_rounds?: number | null;
  orders: TableOrder[];
  running_total: string;
  shared_cart: SharedCart;
}

// Raw (nested) wire shape from GuestCurrentSessionView
interface RawCurrentResponse {
  session: {
    uuid: string;
    // Fix 12: server sends CharField string
    table_number: string | number;
    // SC-513: see TableSessionCurrentResponse.
    table_token?: string | null;
    table_uuid?: string | null;
    // SC-976: see TableSessionCurrentResponse.
    table_tokens?: string[] | null;
    table_uuids?: string[] | null;
    mode: 'ordering' | 'buffet';
    covers: number;
    round_count: number;
    cooldown_seconds_remaining: number;
    expires_at: string;
    shared_table_cart_enabled?: boolean;
    // Spec 2026-08-28 paid-table-ordering-lock : voir TableSessionCurrentResponse.
    ordering_locked?: boolean;
    // DF-0586 : voir TableSessionCurrentResponse.
    holds_a_service?: boolean;
    // SC-221/223: present for buffet sessions, null/absent otherwise.
    buffet_round_item_budget?: number | null;
    buffet_max_rounds?: number | null;
  };
  orders: TableOrder[];
  running_total: string;
  shared_cart?: SharedCart;
}

// ---------------------------------------------------------------------------
// Paiement autonome de la table (QR sur place, Stancer)
// spec 2026-08-28-dine-in-qr-self-payment-stancer.md
// ---------------------------------------------------------------------------

export interface TablePaymentAttempt {
  uuid: string;
  status: 'pending' | 'paid' | 'failed' | 'expired' | 'cancelled';
  amount_ttc: string;
  provider: string;
  redirect_url: string | null;
  created_at: string;
}

/** `enabled` est calcule par le serveur (drapeau + fournisseur + cles en base).
 *  Le front ne recompose jamais cette decision, il l'affiche. */
export interface TablePaymentState {
  enabled: boolean;
  reason: string;
  provider: string | null;
  currency: string;
  due_total: string;
  running_total: string;
  /** Deja encaisse sur cette table. Absent tant que sale_api n'est pas
   *  deploye : le bloc « deja regle » ne s'affiche simplement pas. */
  paid_total?: string;
  is_paid: boolean;
  /** Le paiement fermera-t-il la commande du convive ? Faux sans mode serveur :
   *  personne en salle ne pourrait rouvrir la table. Absent (back pas encore
   *  deploye) = on retombe sur l'ancienne regle, « oui en mode ordering ». */
  ordering_will_lock?: boolean;
  pending: TablePaymentAttempt | null;
}

export interface TablePaymentVerifyResult {
  status: 'paid' | 'pending' | 'failed' | 'expired' | 'unknown';
  applied: boolean;
  payment_status: string;
  due_total: string;
  paid_total?: string;
  is_paid: boolean;
}

function transformJoinResponse(
  raw: JoinTableSessionRawResponse,
  restaurantUuid: string,
  tableToken: string,
  // SC-169: the nickname is client-supplied at join (server doesn't echo it),
  // so it's threaded through from the mutation args and stored on the session.
  nickname?: string,
): TableSession {
  const s = raw.session;
  return {
    guestToken: raw.guest_token,
    restaurantUuid,
    tableToken,
    sessionUuid: s.uuid,
    // Fix 12: coerce to string at transform boundary
    tableNumber: String(s.table_number),
    mode: s.mode,
    covers: s.covers,
    roundCount: s.round_count,
    cooldownSecondsRemaining: s.cooldown_seconds_remaining,
    expiresAt: s.expires_at,
    sharedTableCartEnabled: s.shared_table_cart_enabled ?? false,
    nickname: nickname ?? '',
  };
}

export const tableSessionApiSlice = saleApi.injectEndpoints({
  endpoints: (build) => ({
    resolveTable: build.query<ResolveTableResponse, ResolveTableArgs>({
      queryFn: async ({ restaurantUuid, tableToken }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/table-session/resolve/?restaurant_uuid=${encodeURIComponent(restaurantUuid)}&table_token=${encodeURIComponent(tableToken)}`,
          // No auth header — AllowAny endpoint; do not send consumer Token.
          // SC-199: X-Client-Id makes the scan throttle per-device, not per-IP.
          headers: { Authorization: '', 'X-Client-Id': getClientId() },
        });
        if (result.error) return { error: result.error as never };
        return { data: result.data as ResolveTableResponse };
      },
    }),

    joinTableSession: build.mutation<JoinTableSessionResult, JoinTableSessionArgs>({
      queryFn: async (
        { restaurantUuid, tableToken, covers, nickname, existingGuestToken },
        _api,
        _extra,
        baseQuery,
      ) => {
        // SC-199: X-Client-Id makes the scan throttle per-device, not per-IP.
        const headers: Record<string, string> = { 'X-Client-Id': getClientId() };
        // If there's an existing guest token, send it for idempotent rejoin
        if (existingGuestToken) {
          headers['Authorization'] = `Bearer ${existingGuestToken}`;
        } else {
          // Clear consumer Token auth — this is AllowAny
          headers['Authorization'] = '';
        }
        const body: Record<string, unknown> = {
          restaurant_uuid: restaurantUuid,
          table_token: tableToken,
        };
        if (covers !== undefined) body['covers'] = covers;
        if (nickname !== undefined) body['nickname'] = nickname;

        const result = await baseQuery({
          url: '/sale/order/table-session/join/',
          method: 'POST',
          headers,
          body,
        });
        if (result.error) return { error: result.error as never };
        const raw = result.data as JoinTableSessionRawResponse;
        return { data: { session: transformJoinResponse(raw, restaurantUuid, tableToken, nickname) } };
      },
    }),

    getTableSession: build.query<TableSessionCurrentResponse, GetTableSessionArgs>({
      queryFn: async ({ restaurantUuid: _uuid, sessionUuid: _sid, guestToken }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: '/sale/order/table-session/current/',
          headers: {
            Authorization: `Bearer ${guestToken}`,
          },
        });
        if (result.error) return { error: result.error as never };
        const raw = result.data as RawCurrentResponse;
        // Flatten the nested {session, orders, running_total} into a single object
        // so the component can read fields directly off currentData.
        const flat: TableSessionCurrentResponse = {
          ...raw.session,
          shared_table_cart_enabled: raw.session?.shared_table_cart_enabled ?? false,
          ordering_locked: raw.session?.ordering_locked ?? false,
          orders: raw.orders,
          running_total: raw.running_total,
          shared_cart: raw.shared_cart ?? { lines: [] },
        };
        return { data: flat };
      },
      // Poll every 5 seconds; the component gates this on tab visibility
      // (pollingInterval applied at the component level via useGetTableSessionQuery options)
      providesTags: ['TableSession'],
    }),

    // -- Paiement autonome de la table -------------------------------------
    getTablePayment: build.query<TablePaymentState, { guestToken: string }>({
      queryFn: async ({ guestToken }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: '/sale/order/table-session/payment/',
          headers: { Authorization: `Bearer ${guestToken}` },
        });
        if (result.error) return { error: result.error as never };
        return { data: result.data as TablePaymentState };
      },
      providesTags: ['TableSession'],
    }),

    createTablePayment: build.mutation<
      TablePaymentAttempt,
      // SC-976 (D9): tableToken is the guest's OWN scanned QR — on a merged
      // tablée the server uses it to build the Stancer return URL so payment
      // sends the guest back to the table they're actually sitting at, not
      // the anchor (E-15). Optional: an older server ignores the field.
      { guestToken: string; tableToken?: string }
    >({
      queryFn: async ({ guestToken, tableToken }, _api, _extra, baseQuery) => {
        const body: Record<string, unknown> = {};
        if (tableToken) body['table_token'] = tableToken;
        const result = await baseQuery({
          url: '/sale/order/table-session/payment/',
          method: 'POST',
          headers: { Authorization: `Bearer ${guestToken}` },
          body,
        });
        if (result.error) return { error: result.error as never };
        return { data: result.data as TablePaymentAttempt };
      },
    }),

    verifyTablePayment: build.mutation<
      TablePaymentVerifyResult,
      { guestToken: string; paymentUuid: string }
    >({
      queryFn: async ({ guestToken, paymentUuid }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/table-session/payment/${encodeURIComponent(paymentUuid)}/verify/`,
          method: 'POST',
          headers: { Authorization: `Bearer ${guestToken}` },
          body: {},
        });
        if (result.error) return { error: result.error as never };
        return { data: result.data as TablePaymentVerifyResult };
      },
      invalidatesTags: ['TableSession'],
    }),
  }),
});

export const {
  useResolveTableQuery,
  useJoinTableSessionMutation,
  useGetTableSessionQuery,
  useGetTablePaymentQuery,
  useCreateTablePaymentMutation,
  useVerifyTablePaymentMutation,
} = tableSessionApiSlice;
